<template>
  <div class="mb-5">
    <HMLogo class="logo" />
    <div class="container">
      <div class="row my-3">
        <div class="col-12 my-3" v-if="campaignData && campaignData.logo">
          <span class="d-block">
            {{ $t('SIGNUP.TEXT.OFFICIAL_PARTNER_OF') }}
          </span>
          <img
            class="img-fluid"
            :src="campaignData.logo"
            style="height: 50px"
          />
        </div>
      </div>
    </div>
    <ValidationObserver
      @submit.prevent="signup"
      tag="form"
      class="container content-container"
      ref="form"
      v-slot="{ invalid }"
    >
      <img
        class="submark"
        src="/assets/images/make-it-marvelous-submark_charcoal-coral.png"
      />
      <div
        v-if="affiliateDiscount"
        class="rounded p-3 c-forest bg-forest-light"
      >
        <i class="fas fa-check-circle mr-2"></i>
        <span>
          {{
            $t('AFFILIATES.TEXT.AFFILIATE_DISCOUNT_APPLIED_BANNER', {
              discount: affiliateDiscount,
            })
          }}
        </span>
      </div>
      <div class="row" v-if="invalidPlan">
        <div class="col text-center">
          <h2>
            {{ $t('SIGNUP.TEXT.INVALID_PLAN_SELECTED') }}
          </h2>
          <a class="link mt-3" @click="openPlanModal">
            {{ $t('STUDIO.BUTTON.CHANGE') }}
          </a>
        </div>
      </div>
      <div v-if="selectedPlan">
        <h3>
          {{ selectedPlan.title }}
        </h3>
        <small>
          <a class="link mt-3" @click="openPlanModal" role="button">
            {{ $t('STUDIO.BUTTON.CHANGE') }}
          </a>
        </small>
      </div>
      <div>
        <hr class="my-5" />
        <h4 v-if="selectedPlan">
          {{ selectedPlan.displayName }}
          <span class="c-dark-gray" v-if="freeTrial">
            ({{ $t('SIGNUP.TEXT.X_DAY_FREE_TRIAL', { days: freeTrialDays }) }})
          </span>
        </h4>
        <div class="mt-2">
          <div>
            <i class="fal fa-check c-coral mr-1"></i>
            {{
              $t(
                'UPGRADE.TEXT.YOU_WILL_NOT_BE_CHARGED_UNTIL_YOUR_FREE_TRIAL_ENDS'
              )
            }}
          </div>
          <div>
            <i class="fal fa-check c-coral mr-1"></i>
            {{ $t('UPGRADE.TEXT.CANCEL_ANYTIME') }}
          </div>
        </div>
        <hr class="my-5" />
        <div class="form-group">
          <label for="coupon">
            {{ $t('SIGNUP.LABEL.CREDIT_CARD') }}
          </label>
          <div
            id="card-element"
            ref="card-element"
            :class="{ 'is-invalid': stripeError }"
          ></div>
          <div class="invalid-feedback" v-if="stripeError">
            {{ stripeError }}
          </div>
        </div>
        <div class="form-group">
          <label for="coupon">
            {{ $t('SIGNUP.LABEL.COUPON_CODE') }}
          </label>
          <div class="d-flex" :class="{ 'is-invalid': couponError }">
            <input
              type="text"
              class="form-control"
              name="coupon"
              id="coupon"
              v-model="coupon"
              @change="removeCoupon"
            />
            <span
              class="glyphicon glyphicon-ok form-control-feedback"
              v-if="usingCoupon"
              aria-hidden="true"
            ></span>
            <HMButton
              class="ml-3"
              :disabled="!coupon"
              color="outline-gray"
              @click="applyCoupon"
            >
              {{ $t('SIGNUP.BUTTON.APPLY') }}
            </HMButton>
          </div>
          <span class="invalid-feedback" v-if="couponError">
            {{ couponError }}
          </span>
        </div>
      </div>
      <div class="row submit-group" v-show="selectedPlan">
        <div class="col-md-12">
          <div class="d-flex mt-3 justify-content-between">
            <h2>{{ selectedPlan.name }}</h2>
            <h2>
              {{ getCheckoutFullAmountText }}
            </h2>
          </div>
        </div>
        <div class="col-md-12 text-right" v-if="affiliateDiscount">
          <span
            v-html="
              sanitize(getSelectedPlanAffiliateDiscountCheckoutSummaryText)
            "
          ></span>
        </div>
        <div class="col-md-12 text-right">
          <div>
            <i>{{ discountedText }}</i>
          </div>
          <small>
            <i>{{ $t('SIGNUP.TEXT.ALL_PRICES_ARE_IN_US') }}</i>
          </small>
        </div>
        <div class="col-md-12 text-right" v-if="freeTrial">
          <small>
            <i>{{
              $t('SIGNUP.TEXT.AFTER_TRIAL_ENDS_YOU_WILL_BE_CHARGED', {
                days: freeTrialDays,
              })
            }}</i>
          </small>
        </div>

        <div class="col-md-12">
          <hr class="mt-3" />
          <span class="d-block" v-if="freeTrial">
            {{ $t('SIGNUP.TEXT.X_DAY_FREE_TRIAL', { days: freeTrialDays }) }}
          </span>
          <span class="d-block" v-if="subscriptionType === 'yearly'">
            {{ $t('SIGNUP.TEXT.ANNUAL_PLAN_DISCOUNT_APPLIED') }}
          </span>
          <a
            class="pointer"
            v-if="
              subscriptionType === 'monthly' && selectedPlan.amountChange > 0
            "
            @click="subscriptionType = 'yearly'"
          >
            {{
              $t('SIGNUP.TEXT.YOU_COULD_SAVE_AMOUNT', {
                amount: $formatCurrency(selectedPlan.amountChange),
              })
            }}
          </a>
          <span
            class="d-block"
            v-if="usingCoupon && coupon"
            v-html="sanitize($t('SIGNUP.TEXT.COUPON_CODE_APPLIED', { coupon }))"
          >
          </span>
          <span
            class="d-block"
            v-if="affiliateDiscount"
            v-html="
              sanitize(
                $t('AFFILIATES.TEXT.AFFILIATE_DISCOUNT', {
                  discount: affiliateDiscount,
                })
              )
            "
          >
          </span>
          <hr class="mb-5" />
        </div>
        <div class="col-md-12">
          <HMButton
            class="w-100"
            :class="{
              disabled: !stripeCompleted || invalid || inProgress,
            }"
            color="brand"
            type="submit"
            :loading="inProgress"
          >
            <span class="w-100">{{
              $t('SIGNUP.BUTTON.START_FREE_TRIAL')
            }}</span>
          </HMButton>
        </div>
        <div class="col-md-12">
          <span
            class="text-center d-block my-3"
            v-html="
              sanitize(
                $t('SIGNUP.TEXT.BY_CLICKING_THE_BUTTON_ABOVE', {
                  termsOfUse: config.termsOfUseURL,
                  privacyPolicy: config.privacyPolicyURL,
                  applicationName,
                })
              )
            "
          ></span>
        </div>
      </div>
      <div class="mt-4">
        <font-awesome-icon
          :icon="['far', 'lock-alt']"
          class="mr-1 c-brand-color"
        />
        {{ $t('SIGNUP.TEXT.THIS_IS_A_SECURE_256_BIT') }}
        <span
          class="d-block mt-1 small"
          v-html="sanitize($t('SIGNUP.TEXT.THIS_SITE_IS_PROTECTED_BY'))"
        ></span>
      </div>
    </ValidationObserver>
    <div class="mt-5 d-flex justify-content-center">
      <HMMadeBy />
    </div>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import Vue from 'vue';
import { debounce, isObject } from 'lodash';

import { getPlansData } from '@/web/utils/plans';
import { mapGetters } from 'vuex';
import auth from '@/core/api/modules/auth';
import { style } from '@/web/utils/stripe';
import { trackPurchase, register } from '@/web/services/analytics';
import { errorToMessage, scrollToFirstFormError } from '@/core/utils/errors';
import { loadDeadlineFunnelScript } from '@/web/utils/deadlinefunnel';
import { getConfig } from '@/web/utils/configuration';
import StudentLogin from '@/web/components/signup/StudentLogin.vue';
import SelectPlanModal from '@/web/components/signup/SelectPlanModal.vue';
import { sanitize } from '@/core/utils/utils';

export default Vue.extend({
  name: 'BioUpgrade',
  components: { ValidationObserver },
  data() {
    return {
      subscriptionType: undefined,
      selectedPlanKey: undefined,
      allPlansInclude: [
        'SIGNUP.TEXT.LIVE_STREAMING',
        'SIGNUP.TEXT.SET_YOUR_PRICING',
        'SIGNUP.TEXT.OFFER_COACHING',
        'SIGNUP.TEXT.NO_REV_SHARE',
        'SIGNUP.TEXT.SELL_COURSES',
        'SIGNUP.TEXT.INSTANT_PAYOUTS',
      ],
      teacherForm: {
        yoga_alliance_membership_id: '',
        studio_name: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_repeat: '',
      },
      usingCoupon: false,
      freeTrial: true,
      coupon: null,
      couponError: null,
      inProgress: false,
      stripeCompleted: false,
      stripeError: false,
      emailInfo: null,
      URLInfo: null,
      campaigns: {
        yogaalliance: {
          logo: '/assets/images/yoga-alliance.png',
        },
        lululemon: {
          logo: '/assets/images/lululemon.png',
          brandColor: '#C8102E',
        },
      },
      isUsedEmail: false,
    };
  },
  props: {
    campaign: {
      type: String,
      default: null,
    },
  },
  computed: {
    ...mapGetters('user', [
      'userId',
      'studentId',
      'teacher',
      'userPlans',
      'couponDuration',
      'isLoggedIn',
    ]),
    ...mapGetters({
      affiliateCode: 'globals/affiliateCode',
      affiliateCoupon: 'affiliates/affiliateCoupon',
    }),
    freeTrialDays() {
      return 14;
    },
    plansData() {
      if (!this.subscriptionType) return [];
      return [...(this.userPlans || [])]
        .filter(plan => !(plan.hide_for_affiliates && this.affiliateCode))
        .sort((planA, planB) => planB.tier - planA.tier)
        .map(plan =>
          getPlansData(
            plan,
            this.subscriptionType,
            this.selectedPlanId,
            this.freeTrial,
            this.freeTrialDays,
            this.couponDuration
          )
        );
    },
    selectedPlanId() {
      if (!this.selectedPlanKey) return undefined;
      return this.userPlans?.find(plan => {
        const key = plan.plan_name.toLowerCase();
        return (
          key === this.selectedPlanKey ||
          this.selectedPlanKey === (plan.display_name ?? '').toLowerCase()
        );
      })?.id;
    },
    selectedPlan() {
      return this.plansData?.find(plan => plan.id === this.selectedPlanId);
    },
    isDiscounted() {
      return this.selectedPlan?.discounted;
    },
    isDiscountedForever() {
      return this.couponDuration?.duration === 'forever';
    },
    isDiscountedOnce() {
      if (this.isDiscountedMonths && this.discountedMonthsNum) return true;

      return this.couponDuration?.duration === 'once';
    },
    isDiscountedMonths() {
      return this.couponDuration?.duration === 'repeating';
    },
    discountedMonthsNum() {
      return this.couponDuration?.duration_in_months || 0;
    },
    discountedText() {
      if (!this.isDiscounted) return '';
      if (this.isDiscountedForever) return '';
      let months = 1;
      if (this.isDiscountedMonths) {
        months = this.discountedMonthsNum;
      }
      const discountedPrice = this.selectedPlan.amount;
      const standardPrice = this.selectedPlan.standard;
      const paymentInterval =
        this.subscriptionType === 'monthly' ? 'mo' : 'year';
      if (this.subscriptionType === 'monthly') {
        if (discountedPrice === 0) {
          return this.$tc('SIGNUP.TEXT.MONTHLY_TOTAL_CHECKOUT_FREE', months, {
            discountedPrice,
            months,
            paymentInterval,
            standardPrice,
          });
        }
        return this.$tc('SIGNUP.TEXT.MONTHLY_TOTAL_CHECKOUT', months, {
          discountedPrice,
          months,
          paymentInterval,
          standardPrice,
        });
      } else {
        const years = Math.ceil(months / 12);
        if (discountedPrice === 0) {
          return this.$tc('SIGNUP.TEXT.ANNUAL_TOTAL_CHECKOUT_FREE', years, {
            discountedPrice,
            years,
            paymentInterval,
            standardPrice,
          });
        }
        return this.$tc('SIGNUP.TEXT.ANNUAL_TOTAL_CHECKOUT', years, {
          discountedPrice,
          years,
          paymentInterval,
          standardPrice,
        });
      }
    },
    invalidPlan() {
      return this.userPlans?.length && !this.selectedPlan;
    },
    campaignData() {
      return this.campaigns[this.campaign];
    },
    config() {
      return getConfig();
    },
    applicationName() {
      return this.config.applicationName;
    },
    isNamastreamDomain() {
      return window.location.host.toLowerCase().indexOf('namastream') !== -1;
    },
    affiliateDiscount() {
      const coupon = this.affiliateCoupon;
      if (!coupon) return null;

      let duration = '';
      if (coupon.duration === 'forever') {
        duration = ` ${this.$t(
          'SIGNUP.TEXT.AFFILIATE_COUPON_DURATION_FOREVER'
        )}`;
      } else if (coupon.duration === 'repeating') {
        duration = ` ${this.$tc(
          'SIGNUP.TEXT.AFFILIATE_COUPON_DURATION_REPEATING_VARIANT',
          coupon.duration_in_months,
          { duration: coupon.duration_in_months }
        )}`;
      }

      if (coupon.amount_off) return `$${coupon.amount_off}${duration}`;
      if (coupon.percent_off) return `${coupon.percent_off}%${duration}`;

      return null;
    },
    getAffiliateDiscountedPrice() {
      const coupon = this.affiliateCoupon;
      if (!coupon) return null;
      const plan = this.selectedPlan;

      let discountedPrice = plan.amount;
      if (coupon.amount_off) {
        discountedPrice = Math.max(plan.amount - coupon.amount_off, 0);
      }
      if (coupon.percent_off) {
        discountedPrice =
          Math.round(100 * plan.amount * ((100 - coupon.percent_off) / 100.0)) /
          100;
      }

      return discountedPrice;
    },
    getSelectedPlanAffiliateDiscountCheckoutSummaryText() {
      const coupon = this.affiliateCoupon;
      if (!coupon) return null;
      const plan = this.selectedPlan;

      const discountedPrice = this.getAffiliateDiscountedPrice;

      if (coupon.duration === 'once') {
        return this.$t('SIGNUP.TEXT.AFFILIATE_COUPON_INITIAL_PAYMENT', {
          discountedPrice,
          originalAmount: plan.fullAmountText,
        });
      } else if (coupon.duration === 'forever') {
        // forever coupons are applied for all plans forever and don't need
        // any special text on how long the discount is valid
        return null;
      } else if (coupon.duration === 'repeating') {
        return this.$tc(
          'SIGNUP.TEXT.AFFILIATE_COUPON_DURATION_REPEATING_THEN_ORIGINAL_AMOUNT_VARIANT',
          coupon.duration_in_months,
          {
            duration: coupon.duration_in_months,
            discountedPrice,
            originalAmount: plan.fullAmountText,
          }
        );
      }

      return null;
    },
    getCheckoutFullAmountText() {
      if (this.getAffiliateDiscountedPrice) {
        return this.$t(this.selectedPlan.amountKey, {
          amount: this.getAffiliateDiscountedPrice,
        });
      }

      return this.selectedPlan.fullAmountText;
    },
  },
  methods: {
    sanitize,
    openPlanModal() {
      this.$modal.show(
        SelectPlanModal,
        {
          initalSelectedPlanId: this.selectedPlanId,
          initalSubscriptionType: this.subscriptionType,
          freeTrial: this.freeTrial,
          freeTrialDays: this.freeTrialDays,
        },
        {},
        {
          'select-plan': this.onUrlChange,
        }
      );
    },
    onUrlChange({ planId, subscriptionType }) {
      const plan = this.plansData.find(plan => plan.id === planId);

      if (subscriptionType !== this.subscriptionType) {
        this.$trackOnPage('teacher switched signup type', { subscriptionType });
      }
      if (planId !== this.selectedPlanId) {
        this.$trackOnPage('teacher switched signup plan', {
          plan_id: planId,
          plan_name: plan.name,
        });
      }

      this.$router.push({
        name: 'bio-upgrade',
        query: {
          ...this.$route.query,
          plan: plan.key,
          interval: subscriptionType,
        },
      });
    },
    openLoginModal() {
      this.$modal.show(StudentLogin, {});
    },
    setSubscriptionType(type) {
      this.subscriptionType = type;
      this.$trackOnPage('teacher switched signup type', { type });
    },
    removeCoupon() {
      this.$store.dispatch('user/clearCoupon');
    },
    async validateStudioName() {
      // can't validate studio url without auth headers of an existing account... how to do this?
      console.log('validation needed');
    },
    async applyCoupon() {
      try {
        const coupon = this.coupon;
        const campaign = this.campaign;
        await this.$store.dispatch('user/applyCoupon', { coupon, campaign });
        this.couponError = null;
        this.usingCoupon = true;
      } catch (error) {
        this.couponError = error.response.data?.message;
        this.usingCoupon = false;
      }
    },
    stripeChangeCallback(event) {
      if (event.complete) {
        this.stripeCompleted = true;
        this.stripeError = false;
      } else if (event.error) {
        this.stripeCompleted = false;
        this.stripeError = event.error.message;
      }
    },
    initStripe() {
      this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLIC);
      const elements = this.stripe.elements();
      this.card = elements.create('card', {
        style,
        hidePostalCode: true,
      });
      const interval = setInterval(
        () => {
          if (this.$refs['card-element']) {
            this.card.mount(this.$refs['card-element']);
            this.card.addEventListener('change', this.stripeChangeCallback);
            clearInterval(interval);
          }
        },
        50,
        100
      );
    },

    async signup() {
      this.$refs.form.validate();
      const invalid = this.$refs.form?.flags?.invalid;
      if (invalid) {
        setTimeout(() => {
          scrollToFirstFormError();
        });
      }

      if (!this.stripeCompleted || invalid || this.inProgress) {
        return;
      }

      this.inProgress = true;

      const reCaptchaKey = process.env.VUE_APP_RECAPTCHA_KEY;
      let reCaptchaToken;
      try {
        reCaptchaToken = await window.grecaptcha.execute(reCaptchaKey, {
          action: 'teachersetupintent',
        });
      } catch (error) {
        this.inProgress = false;
        this.$notify({
          group: 'app',
          type: 'error',
          text: this.$t('GENERAL.ERROR.RECAPTCHA_FAILED'),
        });
      }

      const data = {
        ...this.teacherForm,
        coupon: this.coupon || this.affiliateCoupon?.discount_coupon_code,
        campaign: this.campaign,
        plan_id: this.selectedPlanId,
        subscription_type: this.subscriptionType,
        password1: this.teacherForm.password_repeat,
        free_trial: this.freeTrial,
        action_type: 'bio_upgrade',
        token: reCaptchaToken,
      };

      if (this.campaign !== 'yogaalliance')
        delete data.yoga_alliance_membership_id;

      try {
        const setupIntent = await auth.getSetupIntent(data);
        data.stripe_customer_id = setupIntent.stripe_customer_id;
        data.setup_intent_id = setupIntent.setup_intent.id;

        const confirmCard = await this.stripe.confirmCardSetup(
          setupIntent.setup_intent.client_secret,
          { payment_method: { card: this.card } }
        );

        data.default_payment_method = confirmCard.setupIntent.payment_method;

        data.affiliate_ref = this.affiliateCode;

        reCaptchaToken = await window.grecaptcha.execute(reCaptchaKey, {
          action: 'teachersignup',
        });
        data.token = reCaptchaToken;

        if (this.isLoggedIn) await auth.promoteStudentToTeacher(data);
        else await auth.registerTeacher(data);

        const selectedPlan = this.userPlans.find(
          plan => plan.id == this.selectedPlanId
        );

        const planAmount = selectedPlan[`${this.subscriptionType}_amount`];

        if (!this.isLoggedIn) await this.$store.dispatch('user/login', data);

        await this.$store.dispatch('user/loadUserInfo');

        const registerData = {
          first_name: this.teacher?.first_name,
          last_name: this.teacher?.last_name,
          email: this.teacher?.email,
          user_id: this.userId,
          student_id: this.studentId,
          studio_url: this.teacher?.studio_url,
          studio_name: this.teacher?.studio_name,
        };

        const traitsData = {
          coupon: this.coupon || this.affiliateCoupon?.discount_coupon_code,
          affiliate_ref: this.affiliateCoupon?.ref,
          campaign: this.campaign,
          plan_id: this.selectedPlanId,
          plan_name: this.selectedPlan.plan_name,
          plan_amount: planAmount,
          subscription_type: this.subscriptionType,
          studio_name: this.teacher?.studio_name,
        };

        register(this.userId, registerData);
        this.$trackOnPage('teacher signed up', {
          ...registerData,
          ...traitsData,
        });
        trackPurchase(traitsData);

        this.$router.push({ name: 'thank-you' });
      } catch (error) {
        console.error(error);
        const errorMessage = errorToMessage(error.response);
        if (isObject(errorMessage)) {
          this.$refs.form.setErrors(errorMessage);
          scrollToFirstFormError();
        } else {
          this.$notify({
            group: 'app',
            type: 'error',
            text: errorMessage || error,
            duration: -1, // stay ON
          });
        }
        this.inProgress = false;
      }
    },
    loadScripts() {
      loadDeadlineFunnelScript();
    },
    setBrandColor(color) {
      const html = document.querySelector('html');
      html.style.setProperty('--brand-color', color);
    },
    async redirectIfAlreadyPayingStudio() {
      if (!this.isLoggedIn) return;

      await this.$store.state.user.userInfoPromise;
      if (!this.teacher.is_bio) {
        await this.$router.replace({ name: 'settings-adjust-plan' });
      }
    },
  },
  watch: {
    'teacherForm.email': debounce(async function () {
      if (this.$refs.form.fields?.email.valid) {
        try {
          await auth.verifyEmail(this.teacherForm.email);
          this.emailInfo = null;
        } catch (error) {
          this.emailInfo = this.$t('GENERAL.ERROR.ARE_YOU_SURE_THIS_EMAIL');
        }
      } else {
        this.emailInfo = null;
      }

      if (this.$refs.form.fields?.email.valid) {
        try {
          await auth.verifyNewEmail(this.teacherForm.email);
          this.isUsedEmail = false;
        } catch (error) {
          this.isUsedEmail = true;
        }
      } else {
        this.isUsedEmail = false;
      }
    }, 400),
    affiliateCoupon: {
      handler(affiliateCoupon) {
        if (affiliateCoupon && affiliateCoupon.duration === 'forever') {
          this.coupon = this.affiliateCoupon.discount_coupon_code;
          this.applyCoupon();
        }
      },
      immediate: true,
    },
    '$route.query': {
      handler() {
        const { plan, interval } = this.$route.query;
        if (plan && interval) {
          this.selectedPlanKey = plan;
          this.subscriptionType = interval;
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    this.redirectIfAlreadyPayingStudio();
    if (this.isNamastreamDomain && process.env.NODE_ENV === 'production') {
      window.location.hostname = 'app.heymarvelous.com';
    }
    this.campaign = this.campaign || getConfig().signupCampaign;
    this.initStripe();
    this.loadScripts();
    this.removeCoupon();
    await this.$store.dispatch('user/getPlans', this.campaign);
    console.log(this.userPlans);
    const defaultPlan =
      this.userPlans.find(plan => plan.display_name.includes('Solo')) ||
      this.userPlans[0];

    if (
      !this.$route.query.plan ||
      !['yearly', 'monthly'].includes(this.$route.query.interval)
    ) {
      const interval = this.$route.query.interval || 'monthly';
      const plan = (
        this.$route.query.plan || defaultPlan.plan_name
      )?.toLowerCase();
      this.$router.replace({
        name: 'bio-upgrade',
        query: {
          ...this.$route.query,
          plan,
          interval,
        },
      });
    }

    if (this.campaignData?.brandColor) {
      this.setBrandColor(this.campaignData.brandColor);
    }
  },
  destroyed() {
    this.removeCoupon();
    this.setBrandColor(null);
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.logo {
  max-width: 100%;
  height: 17px;
  margin: 2rem 2rem 0 2rem;
}

.content-container {
  max-width: 720px;
  @include media-breakpoint-up(md) {
    margin-top: 50px;
    box-shadow: 0px 16px 24px rgba(0, 0, 0, 0.07);
    border-radius: 6px;
    padding: 50px;
    position: relative;
  }
}

.submark {
  display: none;
  @include media-breakpoint-up(md) {
    display: block;
    position: absolute;
    z-index: 1;
    width: 180px;
    right: -90px;
    top: -90px;
  }
}

.plans {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  flex-direction: row;
  flex-wrap: wrap;
}
</style>
